/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useEffect } from 'react';
import ProvisioningHeader from './ProvisioningHeader';
import styled from 'styled-components';
import ProvisioningTabMenu from './ProvisioningTabMenu';
import { FLEET_MAP_ID, setMapHidden } from '../../store/maps/actions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import appTheme from '../common/theme/appTheme.json';
import { Box } from '@mui/material';
import { Route, Switch } from 'react-router';
import { Redirect, useRouteMatch } from 'react-router-dom';
import ServiceTierChangeRequests from './serviceTierChangeRequests/ServiceTierChangeRequests';
import Organizations from './organizations/Organizations';
import { Authorities } from '../../shared/entities/user';
import { selectPendingServiceRequestsAsArray, selectUserData } from '../../store';
import { areAllowedRolesPresent } from '../../utils/RBACUtils';
import Terminals from './terminals/Terminals';
import Users from './users/Users';
import Contracts from './contracts/Contracts';
import Roles from './roles/Roles';
import Clients from './clients/Clients';

const StyleWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export interface TabConfig {
  // The title of the tab
  title: string;
  // Route to sub-view
  route: string;
  // The content to display when the tab is chosen
  content: JSX.Element;
  // Tab is displayed if this value is unset, or if the user has any of these roles
  restrictedToRoles?: Authorities[];
}

const ProvisioningContainer = () => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const userData = useSelector(selectUserData, shallowEqual);
  const pendingServiceChangeRequests = useSelector(selectPendingServiceRequestsAsArray, shallowEqual);

  // If you need to add a new tab add a TabConfig definition below
  const PROVISIONING_TABS: TabConfig[] = [
    {
      title: 'Terminals',
      route: 'terminals',
      content: <Terminals />,
      restrictedToRoles: [Authorities.ROLE_PROVISIONING_RW],
    },
    {
      title: 'Users',
      route: 'users',
      content: <Users />,
      restrictedToRoles: [Authorities.ROLE_PROVISIONING_RW],
    },
    {
      title: 'Organizations',
      route: 'organizations',
      content: <Organizations />,
      restrictedToRoles: [Authorities.ROLE_PROVISIONING_RW],
    },
    {
      title: 'Contracts',
      route: 'contracts',
      content: <Contracts />,
      restrictedToRoles: [Authorities.ROLE_PROVISIONING_RW],
    },
    {
      title: `Service Tier Change Requests (${pendingServiceChangeRequests.length})`,
      route: 'service-tier-change-requests',
      content: <ServiceTierChangeRequests />,
      restrictedToRoles: [Authorities.ROLE_TDS_STCR_APPROVER],
    },
    {
      title: 'Roles',
      route: 'roles',
      content: <Roles />,
      restrictedToRoles: [Authorities.ROLE_TDS_ROLE_RW],
    },
    {
      title: 'Clients',
      route: 'clients',
      content: <Clients />,
      restrictedToRoles: [Authorities.ROLE_TDS_CLIENT_RW],
    },
  ];

  /**
   * Returns true if a tab should be displayed based on TabConfig above, and the logged-in users' list of roles.
   * @param tabConfig TabConfig for current tab.
   */
  const allowedForRole = (tabConfig: TabConfig) => {
    // If not configured, then tab is viewable by all.
    if (!tabConfig.restrictedToRoles) return true;

    // If userData is missing, then tab is not viewable.
    if (!userData) return false;

    // Otherwise, visibility is determined by roles in JWT.
    return areAllowedRolesPresent(userData, ...tabConfig.restrictedToRoles);
  };

  // only show tabs allowed for the current user
  const tabsAllowedForCurrentUser = PROVISIONING_TABS.filter(allowedForRole);

  useEffect(() => {
    // hide the fleet/FTV view map in the case of refresh on provisioning page
    dispatch(setMapHidden(FLEET_MAP_ID)(true));
  }, [dispatch]);

  return (
    <StyleWrapper>
      <ProvisioningHeader />
      <ProvisioningTabMenu tabs={tabsAllowedForCurrentUser} />
      <ContainerBox path={path} tabsAllowedForCurrentUser={tabsAllowedForCurrentUser} />
    </StyleWrapper>
  );
};

export default ProvisioningContainer;

export interface ContainerBoxProps {
  path: any,
  tabsAllowedForCurrentUser: TabConfig[];
}

export const ContainerBox = (props: ContainerBoxProps) => {
  return (
    <Box
      sx={{
        backgroundColor: appTheme.secondaryBackgroundColor,
        flex: '1 1 auto',
        padding: '16px',
      }}
    >
      <Switch>
        {/* When user goes to provisioning/, push them to the first available sub-route */}
        <Redirect exact from={`${props.path}/`} to={`${props.path}/${props.tabsAllowedForCurrentUser[0].route}`} />

        {/* Render routes for all tabs available to this user */}
        {props.tabsAllowedForCurrentUser.map(tabConfig => (
          <Route path={`${props.path}/${tabConfig.route}`} key={tabConfig.route}>
            {tabConfig.content}
          </Route>
        ))}
      </Switch>
    </Box>
  );
};
